import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Wallet, Trash2, Star, StarOff, Plus, CreditCard } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { useError } from './ErrorContext';
import {
    getBkashAccountsUrl,
    getBkashAccountPostUrl,
    getBkashAccountPutUrl,
    getBkashAccountDeleteUrl,
    getSavedCardsUrl,
    getCardSetupUrl,
    getSavedCardPutUrl,
    getSavedCardDeleteUrl,
    getTransactionsUrl,
} from '../utils/UrlUtilities';
import {
    constructBkashAccountPayload,
    constructBkashAccountUpdatePayload,
} from '../utils/PayloadUtils';
import {
    TRANSACTION_TYPE_LABELS,
    TRANSACTION_STATUS_LABELS,
    TRANSACTION_STATUS_COLORS,
    itemsPerPageOptions,
    defaultPageSize,
} from '../utils/Constants';
import { formatBdPst } from '../utils/DateUtilities';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import './components.css';

// The country code is fixed (+880); the user enters the 10-digit local number
// (e.g. 1712345678). Server (crypto.normalize_bkash) is authoritative.
const isValidLocal = (local) => /^1\d{9}$/.test((local || '').replace(/\D/g, ''));

const toIso = (d) => (d ? d.toISOString() : undefined);

export default function PaymentManagement() {
    const CallApi = useApiCaller();
    const { userState } = useUser();
    const { showSuccess } = useError();
    const [searchParams, setSearchParams] = useSearchParams();

    // --- bKash accounts ---
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [newLocal, setNewLocal] = useState(''); // 10-digit local part, sent as +880XXXXXXXXXX
    const [newLabel, setNewLabel] = useState('');
    const [adding, setAdding] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // --- saved cards (Stripe) ---
    const [cards, setCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [addingCard, setAddingCard] = useState(false);
    const [cardDeleteTarget, setCardDeleteTarget] = useState(null);
    const [cardDeleting, setCardDeleting] = useState(false);

    // --- transactions ---
    const [transactions, setTransactions] = useState([]);
    const [total, setTotal] = useState(0);
    const [txnLoading, setTxnLoading] = useState(false);
    const [typeFilter, setTypeFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const fetchAccounts = useCallback(async () => {
        setAccountsLoading(true);
        const res = await CallApi({ url: getBkashAccountsUrl(), method: 'GET', token: true });
        if (res.success) setAccounts(res.data || []);
        setAccountsLoading(false);
    }, [CallApi]);

    const fetchCards = useCallback(async () => {
        setCardsLoading(true);
        const res = await CallApi({ url: getSavedCardsUrl(), method: 'GET', token: true });
        if (res.success) setCards(res.data || []);
        setCardsLoading(false);
    }, [CallApi]);

    const fetchTransactions = useCallback(async () => {
        setTxnLoading(true);
        const params = { page: currentPage, limit: pageSize };
        if (typeFilter && typeFilter !== 'all') params.transaction_type = typeFilter;
        if (dateFrom) params.date_from = toIso(dateFrom);
        if (dateTo) params.date_to = toIso(dateTo);
        const res = await CallApi({ url: getTransactionsUrl(params), method: 'GET', token: true });
        if (res.success) {
            setTransactions(res.data?.transactions || []);
            setTotal(res.data?.total || 0);
        } else {
            setTransactions([]);
            setTotal(0);
        }
        setTxnLoading(false);
    }, [CallApi, currentPage, pageSize, typeFilter, dateFrom, dateTo]);

    useEffect(() => {
        if (userState?.isLoggedIn) fetchAccounts();
    }, [userState?.isLoggedIn, fetchAccounts]);

    useEffect(() => {
        if (userState?.isLoggedIn) fetchCards();
    }, [userState?.isLoggedIn, fetchCards]);

    // Returning from Stripe's hosted card-setup page (?card_setup=success): confirm + refresh.
    useEffect(() => {
        const status = searchParams.get('card_setup');
        if (!status) return;
        if (status === 'success') {
            showSuccess('Card saved.');
            fetchCards();
        }
        searchParams.delete('card_setup');
        setSearchParams(searchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userState?.isLoggedIn) fetchTransactions();
    }, [userState?.isLoggedIn, fetchTransactions]);

    const handleAdd = async () => {
        if (!isValidLocal(newLocal)) return;
        setAdding(true);
        const fullNumber = '+880' + newLocal.replace(/\D/g, '');
        const res = await CallApi({
            url: getBkashAccountPostUrl(),
            method: 'POST',
            payload: constructBkashAccountPayload({ accountNumber: fullNumber, label: newLabel.trim() }),
            token: true,
        });
        setAdding(false);
        if (res.success) {
            setNewLocal('');
            setNewLabel('');
            showSuccess('bKash account added.');
            fetchAccounts();
        }
    };

    const handleSetDefault = async (account) => {
        const res = await CallApi({
            url: getBkashAccountPutUrl(account.account_id),
            method: 'PUT',
            payload: constructBkashAccountUpdatePayload({ isDefault: true }),
            token: true,
        });
        if (res.success) {
            showSuccess('Default account updated.');
            fetchAccounts();
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        const res = await CallApi({
            url: getBkashAccountDeleteUrl(deleteTarget.account_id),
            method: 'DELETE',
            token: true,
        });
        setDeleting(false);
        setDeleteTarget(null);
        if (res.success) {
            showSuccess('bKash account removed.');
            fetchAccounts();
        }
    };

    const handleAddCard = async () => {
        setAddingCard(true);
        const res = await CallApi({ url: getCardSetupUrl(), method: 'POST', token: true });
        if (res.success && res.data?.redirect_url) {
            window.location = res.data.redirect_url; // Stripe hosted card-setup page
            return;
        }
        setAddingCard(false);
    };

    const handleSetDefaultCard = async (card) => {
        const res = await CallApi({
            url: getSavedCardPutUrl(card.card_id),
            method: 'PUT',
            payload: { is_default: true },
            token: true,
        });
        if (res.success) {
            showSuccess('Default card updated.');
            fetchCards();
        }
    };

    const handleDeleteCard = async () => {
        if (!cardDeleteTarget) return;
        setCardDeleting(true);
        const res = await CallApi({
            url: getSavedCardDeleteUrl(cardDeleteTarget.card_id),
            method: 'DELETE',
            token: true,
        });
        setCardDeleting(false);
        setCardDeleteTarget(null);
        if (res.success) {
            showSuccess('Card removed.');
            fetchCards();
        }
    };

    if (!userState?.isLoggedIn) {
        return (
            <div className="container py-5 text-center" style={{ minHeight: '60vh' }}>
                <h4 className="text-muted">Please log in to manage your payments.</h4>
            </div>
        );
    }

    const localInvalid = newLocal.length > 0 && !isValidLocal(newLocal);

    return (
        <div className="container py-4" style={{ maxWidth: 900, minHeight: '70vh' }}>
            <h2 className="fw-bold mb-4" style={{ color: '#4c1d95' }}>
                <Wallet size={24} className="me-2" />
                Payment Management
            </h2>

            {/* Section 1: Saved cards (Stripe) */}
            <div className="card mb-4" style={{ borderColor: '#ede9fe' }}>
                <div className="card-body">
                    <h5 className="card-title mb-3"><CreditCard size={18} className="me-2" />Saved Cards</h5>
                    {cardsLoading ? (
                        <div className="text-center p-3"><div className="spinner-border text-primary" role="status" /></div>
                    ) : cards.length === 0 ? (
                        <p className="text-muted">No saved cards yet. Add one to check out faster next time.</p>
                    ) : (
                        <ul className="list-group mb-3">
                            {cards.map((c) => (
                                <li key={c.card_id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>
                                        <span className="fw-semibold text-capitalize">{c.brand || 'Card'}</span>
                                        <span className="ms-2">•••• {c.last4}</span>
                                        {c.exp_month && c.exp_year && (
                                            <span className="text-muted ms-2 small">exp {String(c.exp_month).padStart(2, '0')}/{String(c.exp_year).slice(-2)}</span>
                                        )}
                                        {c.is_default && <span className="badge bg-success ms-2">Default</span>}
                                    </span>
                                    <span className="d-flex gap-2">
                                        {!c.is_default && (
                                            <button className="btn btn-outline-secondary btn-sm" title="Set as default" onClick={() => handleSetDefaultCard(c)}>
                                                <Star size={16} /> Set default
                                            </button>
                                        )}
                                        {c.is_default && (
                                            <span className="text-muted small d-flex align-items-center"><StarOff size={14} className="me-1" /> current default</span>
                                        )}
                                        <button className="btn btn-outline-danger btn-sm" title="Delete" onClick={() => setCardDeleteTarget(c)}>
                                            <Trash2 size={16} />
                                        </button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button className="btn btn-primary" onClick={handleAddCard} disabled={addingCard}>
                        <Plus size={16} className="me-1" />
                        {addingCard ? 'Opening secure page…' : 'Add card'}
                    </button>
                    <div className="text-muted small mt-2">
                        Cards are stored securely with Stripe — Kobra Prep never sees your card number.
                    </div>
                </div>
            </div>

            {/* Section 2: PayPal (coming soon) */}
            <div className="card mb-4" style={{ borderColor: '#ede9fe', opacity: 0.75 }}>
                <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="card-title mb-1"><i className="bi bi-paypal me-2" />PayPal</h5>
                        <div className="text-muted small">Link a PayPal account to pay — coming soon.</div>
                    </div>
                    <span className="badge bg-secondary">Coming soon</span>
                </div>
            </div>

            {/* Section 3: bKash accounts */}
            <div className="card mb-4" style={{ borderColor: '#ede9fe' }}>
                <div className="card-body">
                    <h5 className="card-title mb-3">bKash Accounts</h5>

                    {accountsLoading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border text-primary" role="status" />
                        </div>
                    ) : accounts.length === 0 ? (
                        <p className="text-muted">No bKash accounts yet. Add one below.</p>
                    ) : (
                        <ul className="list-group mb-3">
                            {accounts.map((a) => (
                                <li
                                    key={a.account_id}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                >
                                    <span>
                                        <span className="fw-semibold">{a.masked_number}</span>
                                        {a.label && <span className="text-muted ms-2">({a.label})</span>}
                                        {a.is_default && (
                                            <span className="badge bg-success ms-2">Default</span>
                                        )}
                                    </span>
                                    <span className="d-flex gap-2">
                                        {!a.is_default && (
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                title="Set as default"
                                                onClick={() => handleSetDefault(a)}
                                            >
                                                <Star size={16} /> Set default
                                            </button>
                                        )}
                                        {a.is_default && (
                                            <span className="text-muted small d-flex align-items-center">
                                                <StarOff size={14} className="me-1" /> current default
                                            </span>
                                        )}
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            title="Delete"
                                            onClick={() => setDeleteTarget(a)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Add form */}
                    <div className="row g-2 align-items-end">
                        <div className="col-sm-5">
                            <label className="form-label small text-muted mb-0">bKash number</label>
                            <div className="input-group">
                                <span className="input-group-text" title="Bangladesh (+880)">
                                    <span className="me-1" aria-hidden="true">🇧🇩</span> +880
                                </span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={10}
                                    className={`form-control${localInvalid ? ' is-invalid' : ''}`}
                                    placeholder="e.g. 1712345678"
                                    value={newLocal}
                                    onChange={(e) => setNewLocal(e.target.value.replace(/\D/g, ''))}
                                />
                                {localInvalid && (
                                    <div className="invalid-feedback">
                                        Enter the 10-digit number after +880 (e.g. 1712345678).
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-sm-4">
                            <label className="form-label small text-muted mb-0">Label (optional)</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Personal"
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                            />
                        </div>
                        <div className="col-sm-3">
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleAdd}
                                disabled={adding || !isValidLocal(newLocal)}
                            >
                                <Plus size={16} className="me-1" />
                                {adding ? 'Adding…' : 'Add account'}
                            </button>
                        </div>
                    </div>
                    <div className="text-muted small mt-2">
                        Only your bKash number is stored (encrypted) — never your PIN or password.
                    </div>
                </div>
            </div>

            {/* Section 2: Transaction history */}
            <div className="card" style={{ borderColor: '#ede9fe' }}>
                <div className="card-body">
                    <h5 className="card-title mb-3">Transaction History</h5>

                    {/* Filters */}
                    <div className="row g-2 align-items-end mb-3">
                        <div className="col-sm-3">
                            <label className="form-label small text-muted mb-0">Type</label>
                            <select
                                className="form-select form-select-sm"
                                value={typeFilter}
                                onChange={(e) => {
                                    setTypeFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="all">All</option>
                                {Object.entries(TRANSACTION_TYPE_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-sm-3">
                            <label className="form-label small text-muted mb-0 d-block">From</label>
                            <DatePicker
                                selected={dateFrom}
                                onChange={(d) => { setDateFrom(d); setCurrentPage(1); }}
                                className="form-control form-control-sm"
                                dateFormat="yyyy-MM-dd"
                                placeholderText="From date"
                                isClearable
                            />
                        </div>
                        <div className="col-sm-3">
                            <label className="form-label small text-muted mb-0 d-block">To</label>
                            <DatePicker
                                selected={dateTo}
                                onChange={(d) => { setDateTo(d); setCurrentPage(1); }}
                                className="form-control form-control-sm"
                                dateFormat="yyyy-MM-dd"
                                placeholderText="To date"
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover table-striped mb-0">
                            <thead className="table-secondary">
                                <tr>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Account</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {txnLoading ? (
                                    <tr><td colSpan={5} className="text-center p-3">
                                        <div className="spinner-border text-primary" role="status" />
                                    </td></tr>
                                ) : transactions.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center text-muted p-3">No transactions.</td></tr>
                                ) : (
                                    transactions.map((t) => (
                                        <tr key={t.transaction_id}>
                                            <td>
                                                {TRANSACTION_TYPE_LABELS[t.transaction_type] || t.transaction_type}
                                                {t.plan_name ? <span className="text-muted"> — {t.plan_name}</span> : null}
                                            </td>
                                            <td>৳{Number(t.amount_bdt).toFixed(2)}</td>
                                            <td>
                                                <span className={`badge bg-${TRANSACTION_STATUS_COLORS[t.status] || 'secondary'}`}>
                                                    {TRANSACTION_STATUS_LABELS[t.status] || t.status}
                                                </span>
                                            </td>
                                            <td>
                                                {t.masked_account || '—'}
                                                {t.account_deleted && (
                                                    <span className="text-muted small ms-1">(deleted)</span>
                                                )}
                                            </td>
                                            <td className="small">{formatBdPst(t.created_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="txnPageSize" className="form-label text-muted mb-0 small">Per page:</label>
                            <select
                                id="txnPageSize"
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                                className="form-select form-select-sm w-auto"
                            >
                                {itemsPerPageOptions.map((size) => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-outline-secondary btn-sm rounded-pill"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            >
                                Prev
                            </button>
                            <span className="text-muted small">Page {currentPage} of {totalPages}</span>
                            <button
                                className="btn btn-outline-secondary btn-sm rounded-pill"
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmDeleteDialog
                open={Boolean(deleteTarget)}
                title="Remove bKash account?"
                message={
                    deleteTarget
                        ? `Remove ${deleteTarget.masked_number}? ${
                              accounts.length === 1
                                  ? "This is your only bKash account — you'll have no active account after this."
                                  : deleteTarget.is_default
                                  ? 'Another account will become your default.'
                                  : ''
                          }`
                        : ''
                }
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
                busy={deleting}
            />

            <ConfirmDeleteDialog
                open={Boolean(cardDeleteTarget)}
                title="Remove saved card?"
                message={
                    cardDeleteTarget
                        ? `Remove ${cardDeleteTarget.brand || 'card'} •••• ${cardDeleteTarget.last4}? You can add it again later.`
                        : ''
                }
                onConfirm={handleDeleteCard}
                onCancel={() => setCardDeleteTarget(null)}
                busy={cardDeleting}
            />
        </div>
    );
}
